import { Card, Col, DatePicker, Form, Input, Result, Row, Select, Skeleton, Tooltip, TreeSelect } from "antd";
import PageHeader from "components/PageHeader";
import { PrimaryButton, PrimaryButtonOutLine } from "components/PrimaryButton";
import { DATE_TIME_FORMAT } from "config/constants";
import {
  APPLY_SCOPE_OPTIONS,
  LOYALTY_APPLY_SCOPE,
  LOYALTY_FIELD_LIMITS,
  PACKAGE_FORM_MODE,
} from "features/LoyaltyPackage/core/constants";
import {
  getPackageDatePickerBounds,
  getPackageDateRangePickerPanelValue,
  isPackageDateDisabled,
  normalizeLoyaltyMoment,
  toFormDateRangeMoment,
} from "features/LoyaltyPackage/core/utils";
import { t } from "i18next";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import MilestoneSection from "./components/MilestoneSection";
import PackageConfirmModal from "./components/PackageConfirmModal";
import ProductTable from "./components/ProductTable";
import {
  LoyaltyConfirmDialog,
  LOYALTY_CONFIRM_ICON,
  LoyaltySection,
} from "features/LoyaltyPackage/presentation/components";
import usePackageForm from "./usePackageForm";

import "../ProgramFormModal/index.scss";
import "./index.scss";

const { RangePicker } = DatePicker;

/** Wrapper moment ổn định cho antd 4 RangePicker — tránh crash rc-picker (date.month is not a function) */
function PackageDateRangePicker({ value, onChange, onOpenChange, selectedProgram, ...rest }) {
  const startMs = value?.[0]?.valueOf?.() ?? value?.[0]?._d?.valueOf?.();
  const endMs = value?.[1]?.valueOf?.() ?? value?.[1]?._d?.valueOf?.();
  const normalizedValue = useMemo(() => toFormDateRangeMoment(value), [startMs, endMs]); // eslint-disable-line react-hooks/exhaustive-deps

  const bounds = useMemo(() => getPackageDatePickerBounds(selectedProgram), [selectedProgram]);

  const [openHackValue, setOpenHackValue] = useState(null);
  // Phía đang chọn ('start' | 'end') — dùng để chặn ngày kết thúc < hôm nay (start vẫn cho quá khứ)
  const [activeRange, setActiveRange] = useState("start");

  const displayValue = openHackValue ?? normalizedValue ?? null;

  const disabledDate = useCallback(
    (current) => {
      if (isPackageDateDisabled(current, selectedProgram)) return true;
      // Ngày kết thúc phải >= hôm nay (ngày bắt đầu vẫn cho phép quá khứ)
      if (activeRange === "end" && current && current.isBefore(moment().startOf("day"), "day")) return true;
      return false;
    },
    [selectedProgram, activeRange]
  );

  const handleOpenChange = (open) => {
    if (open) {
      setActiveRange("start");
      const [start, end] = getPackageDateRangePickerPanelValue(bounds, normalizedValue);
      setOpenHackValue(
        normalizedValue ? [moment(normalizedValue[0].valueOf()), moment(normalizedValue[1].valueOf())] : [start, end]
      );
    } else {
      setOpenHackValue(null);
      setActiveRange("start");
    }
    onOpenChange?.(open);
  };

  const handleCalendarChange = (dates) => {
    // Đã chọn ngày bắt đầu, chưa chọn ngày kết thúc → chuyển sang chế độ chọn end (chặn quá khứ)
    setActiveRange(dates?.[0] && !dates?.[1] ? "end" : "start");
  };

  const handleChange = (dates) => {
    setOpenHackValue(null);
    if (!dates?.[0] || !dates?.[1]) {
      onChange?.(undefined);
      return;
    }
    // Chỉ chọn ngày — mặc định start 00:00:00, end 23:59:59
    const start = moment(dates[0].valueOf()).startOf("day");
    const end = moment(dates[1].valueOf()).endOf("day").milliseconds(0);
    onChange?.([start, end]);
  };

  return (
    <RangePicker
      {...rest}
      className={["package-form-date-range-picker", rest.className].filter(Boolean).join(" ")}
      value={displayValue}
      onOpenChange={handleOpenChange}
      onCalendarChange={handleCalendarChange}
      onChange={handleChange}
      format={DATE_TIME_FORMAT}
      disabledDate={disabledDate}
    />
  );
}

const fieldLabel = (label, required = true) => (
  <span className="loyalty-field-label">
    {label} {required && <span className="loyalty-required">*</span>}
  </span>
);

function CategoryTreeSelect({ value, onChange, treeData = [], loading, onCategoryWillChange, onLoadOptions }) {
  const [searchValue, setSearchValue] = useState("");
  const [treeSelectKey, setTreeSelectKey] = useState(0);

  const handleChange = (newId) => {
    if (onCategoryWillChange?.(newId)) {
      return;
    }
    setSearchValue("");
    onChange?.(newId);
  };

  const handleDropdownVisibleChange = (open) => {
    if (open) {
      onLoadOptions?.();
    }
    if (!open) {
      setSearchValue("");
      // TreeSelect antd giữ tree đã filter nội bộ dù searchValue đã clear — remount khi đóng để lần mở sau hiện đủ
      setTreeSelectKey((key) => key + 1);
    }
  };

  const filterTreeNode = (search, node) => {
    if (!search?.trim()) return true;
    return node?.label?.toLowerCase().includes(search.toLowerCase());
  };

  return (
    <TreeSelect
      key={treeSelectKey}
      value={value}
      onChange={handleChange}
      treeData={treeData}
      loading={loading}
      placeholder={t("loyaltyPackage.packageForm.categoryPlaceholder")}
      showSearch
      searchValue={searchValue}
      onSearch={setSearchValue}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      allowClear
      treeDefaultExpandAll
      style={{ width: "100%" }}
      dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
      filterTreeNode={filterTreeNode}
      getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
    />
  );
}

function ApplyScopeSelect({ value, onChange, onScopeWillChange, ...rest }) {
  const handleChange = (newScope) => {
    if (onScopeWillChange?.(newScope)) {
      return;
    }
    onChange?.(newScope);
  };

  return <Select value={value} onChange={handleChange} {...rest} />;
}

function findCategoryLabel(options, value) {
  if (!value || !options?.length) return "—";
  for (const opt of options) {
    if (opt.value === value) return opt.label;
    if (opt.children) {
      const found = findCategoryLabel(opt.children, value);
      if (found !== "—") return found;
    }
  }
  return "—";
}

function PackageForm({ mode = PACKAGE_FORM_MODE.CREATE, periodId }) {
  const history = useHistory();
  const {
    form,
    isEditMode,
    notEditable,
    pageLoading,
    milestones,
    milestoneErrors,
    products,
    productError,
    programOptions,
    categoryOptions,
    categoriesLoading,
    programsLoading,
    programsLoadingMore,
    handleProgramDropdownVisibleChange,
    handleProgramPopupScroll,
    handleCategoryDropdownVisibleChange,
    applyScope,
    categoryId,
    selectedProgram,
    timeIntervalOptions,
    confirmVisible,
    cancelVisible,
    pendingFormValues,
    submitting,
    saveDisabled,
    handleMilestoneChange,
    handleAddMilestone,
    handleRemoveMilestone,
    handleApplyScopeChange,
    confirmApplyScopeChange,
    handleCategoryChangeAttempt,
    confirmCategoryChange,
    handleAddProducts,
    handleRemoveProduct,
    handleUpdateProductMilestones,
    handleFormSubmit,
    handleFormSubmitFailed,
    handleConfirmSubmit,
    handleCancel,
    confirmCancel,
    handleFormValuesChange,
    setConfirmVisible,
    setCancelVisible,
  } = usePackageForm({ mode, periodId });

  const [applyScopeConfirm, setApplyScopeConfirm] = useState(null);
  const [applyScopeSelectKey, setApplyScopeSelectKey] = useState(0);
  const [categoryConfirm, setCategoryConfirm] = useState(null);

  const pageTitle =
    mode === PACKAGE_FORM_MODE.EDIT
      ? t("loyaltyPackage.packageForm.editTitle")
      : t("loyaltyPackage.packageForm.createTitle");

  const breadcrumbName =
    mode === PACKAGE_FORM_MODE.EDIT
      ? t("loyaltyPackage.packageForm.editTitle")
      : t("loyaltyPackage.packageForm.createTitle");

  const categoryName = useMemo(() => findCategoryLabel(categoryOptions, categoryId), [categoryOptions, categoryId]);

  const pageHeader = (
    <PageHeader
      pageTitle={pageTitle}
      routes={[
        { path: "/loyalty-package?tab=package", name: t("loyaltyPackage.packageForm.breadcrumbParent") },
        { path: "", name: breadcrumbName },
      ]}
    />
  );

  if (pageLoading) {
    return (
      <div className="package-form-page">
        {pageHeader}
        <div className="package-form-container">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </div>
    );
  }

  if (notEditable) {
    return (
      <div className="package-form-page">
        {pageHeader}
        <div className="package-form-container">
          <Result
            status="403"
            title={t("loyaltyPackage.packageForm.editNotAllowed")}
            extra={
              <PrimaryButton
                title={t("loyaltyPackage.packageForm.backToList")}
                onClick={() => history.push("/loyalty-package?tab=package")}
              />
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="package-form-page">
      {pageHeader}

      <div className="package-form-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          onFinishFailed={handleFormSubmitFailed}
          onValuesChange={handleFormValuesChange}
          className="package-form"
          requiredMark={false}
        >
          <div className="package-form-wrapper-card">
            <div className="package-form-sections">
              <LoyaltySection
                className="package-form-general-section"
                title={t("loyaltyPackage.packageForm.generalInfoSection")}
              >
                <Row gutter={[24, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={fieldLabel(t("loyaltyPackage.form.packageName"))}
                      name="title"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: t("loyaltyPackage.packageForm.nameRequired"),
                        },
                        {
                          max: LOYALTY_FIELD_LIMITS.NAME_MAX_LENGTH,
                          message: t("loyaltyPackage.packageForm.nameMaxLength"),
                        },
                      ]}
                    >
                      <Input placeholder={t("loyaltyPackage.packageForm.namePlaceholder")} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      className="package-form-program-field"
                      label={fieldLabel(t("loyaltyPackage.form.parentProgram"))}
                      name="loyaltyPackageId"
                      rules={[{ required: true, message: t("loyaltyPackage.packageForm.programRequired") }]}
                      extra={
                        selectedProgram ? (
                          <span className="package-form-program-hint">
                            {t("loyaltyPackage.packageForm.programTimeHint")}{" "}
                            {normalizeLoyaltyMoment(selectedProgram.startTime).format(DATE_TIME_FORMAT)} –{" "}
                            {moment(selectedProgram.endTime).milliseconds(0).format(DATE_TIME_FORMAT)}
                          </span>
                        ) : null
                      }
                    >
                      <Select
                        placeholder={t("loyaltyPackage.packageForm.programPlaceholder")}
                        options={programOptions}
                        loading={programsLoading || programsLoadingMore}
                        showSearch
                        optionFilterProp="label"
                        onDropdownVisibleChange={handleProgramDropdownVisibleChange}
                        onPopupScroll={handleProgramPopupScroll}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={fieldLabel(t("loyaltyPackage.packageForm.timeIntervalLabel"))}
                      name="timeInterval"
                      rules={[{ required: true, message: t("loyaltyPackage.packageForm.timeIntervalRequired") }]}
                    >
                      <Select
                        placeholder={t("loyaltyPackage.packageForm.timeIntervalPlaceholder")}
                        options={timeIntervalOptions}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={fieldLabel(t("loyaltyPackage.form.dateRange"))}
                      name="dateRange"
                      rules={[{ required: true, message: t("loyaltyPackage.packageForm.dateRangeRequired") }]}
                      getValueProps={(val) => ({ value: toFormDateRangeMoment(val) })}
                      getValueFromEvent={(dates) => {
                        if (!dates?.[0] || !dates?.[1]) return undefined;
                        return [moment(dates[0].valueOf()), moment(dates[1].valueOf())];
                      }}
                    >
                      <PackageDateRangePicker
                        selectedProgram={selectedProgram}
                        style={{ width: "100%" }}
                        placeholder={[
                          t("loyaltyPackage.programForm.dateStartPlaceholder"),
                          t("loyaltyPackage.programForm.dateEndPlaceholder"),
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={fieldLabel(t("loyaltyPackage.form.category"))}
                      name="categoryId"
                      rules={[{ required: true, message: t("loyaltyPackage.packageForm.categoryRequired") }]}
                    >
                      <CategoryTreeSelect
                        treeData={categoryOptions}
                        loading={categoriesLoading}
                        onLoadOptions={handleCategoryDropdownVisibleChange}
                        onCategoryWillChange={(newId) => {
                          if (handleCategoryChangeAttempt(newId)) {
                            setCategoryConfirm(newId);
                            return true;
                          }
                          return false;
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={fieldLabel(t("loyaltyPackage.packageForm.applyScopeLabel"))}
                      name="applyScope"
                      rules={[{ required: true, message: t("loyaltyPackage.packageForm.applyScopeRequired") }]}
                    >
                      <ApplyScopeSelect
                        key={applyScopeSelectKey}
                        placeholder={t("loyaltyPackage.packageForm.applyScopePlaceholder")}
                        options={APPLY_SCOPE_OPTIONS}
                        allowClear={false}
                        onScopeWillChange={(newScope) => {
                          const result = handleApplyScopeChange(newScope);
                          if (result?.needConfirm) {
                            setApplyScopeConfirm({
                              pendingScope: newScope,
                              revertScope: applyScope ?? LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS,
                            });
                            return true;
                          }
                          return false;
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </LoyaltySection>

              <MilestoneSection
                className="package-form-milestone-section"
                milestones={milestones}
                milestoneErrors={milestoneErrors}
                onMilestoneChange={handleMilestoneChange}
                onAddMilestone={handleAddMilestone}
                onRemoveMilestone={handleRemoveMilestone}
              />

              <ProductTable
                className="package-form-product-section"
                products={products}
                milestones={milestones}
                applyScope={applyScope}
                categoryId={categoryId}
                productError={productError}
                onAddProducts={handleAddProducts}
                onRemoveProduct={handleRemoveProduct}
                onUpdateProductMilestones={handleUpdateProductMilestones}
              />
            </div>
          </div>

          <div className="package-form-footer-bar">
            <Card className="package-form-footer" bordered={false}>
              <PrimaryButtonOutLine
                title={t("loyaltyPackage.form.cancel")}
                onClick={handleCancel}
                style={{
                  color: "#2246dc",
                  borderColor: "#2246dc",
                  minWidth: 120,
                  height: 40,
                  padding: "4px 24px",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              />
              {saveDisabled ? (
                <Tooltip title={t("loyaltyPackage.packageForm.saveDisabledHint")}>
                  <span className="package-form-save-btn-wrap">
                    <PrimaryButton
                      title={t("loyaltyPackage.form.save")}
                      htmlType="submit"
                      loading={submitting}
                      disabled
                      style={{
                        minWidth: 160,
                        height: 40,
                        padding: "4px 24px",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    />
                  </span>
                </Tooltip>
              ) : (
                <PrimaryButton
                  title={isEditMode ? t("loyaltyPackage.form.save") : t("loyaltyPackage.packageForm.submitCreate")}
                  htmlType="submit"
                  loading={submitting}
                  style={{ minWidth: 160, height: 40, padding: "4px 24px", fontSize: 14, fontWeight: 600 }}
                />
              )}
            </Card>
          </div>
        </Form>
      </div>

      <PackageConfirmModal
        visible={confirmVisible}
        mode={mode}
        data={pendingFormValues}
        milestones={milestones}
        products={products}
        programTitle={selectedProgram?.title}
        categoryName={categoryName}
        loading={submitting}
        onCancel={() => setConfirmVisible(false)}
        onConfirm={handleConfirmSubmit}
      />

      <LoyaltyConfirmDialog
        visible={cancelVisible}
        message={
          isEditMode
            ? t("loyaltyPackage.packageForm.confirmCancelEditMessage")
            : t("loyaltyPackage.packageForm.confirmCancelCreateMessage")
        }
        onCancel={() => setCancelVisible(false)}
        onConfirm={confirmCancel}
      />

      <LoyaltyConfirmDialog
        visible={Boolean(applyScopeConfirm)}
        message={t("loyaltyPackage.packageForm.confirmApplyScopeContent")}
        onCancel={() => {
          if (applyScopeConfirm?.revertScope != null) {
            form.setFieldsValue({ applyScope: applyScopeConfirm.revertScope });
          }
          form.setFields([{ name: "applyScope", errors: [] }]);
          setApplyScopeConfirm(null);
          setApplyScopeSelectKey((key) => key + 1);
        }}
        onConfirm={() => {
          confirmApplyScopeChange(applyScopeConfirm?.pendingScope);
          setApplyScopeConfirm(null);
        }}
      />

      <LoyaltyConfirmDialog
        visible={Boolean(categoryConfirm)}
        iconType={isEditMode ? LOYALTY_CONFIRM_ICON.DELETE : LOYALTY_CONFIRM_ICON.QUESTION}
        message={
          isEditMode
            ? t("loyaltyPackage.packageForm.confirmChangeCategoryEditMessage")
            : t("loyaltyPackage.product.confirmChangeCategoryContent")
        }
        onCancel={() => setCategoryConfirm(null)}
        onConfirm={() => {
          confirmCategoryChange(categoryConfirm);
          setCategoryConfirm(null);
        }}
      />
    </div>
  );
}

export default PackageForm;
